import React, { useEffect, useRef } from 'react';
import './AuthLanding.css';

export type VideoBackgroundVariant = 'splash' | 'scrollable';

interface VideoBackgroundProps {
  children: React.ReactNode;
  /** Content below the hero (video + main). Enables a tall, scrollable landing page. */
  afterHero?: React.ReactNode;
  /** Hide the default legal footer (e.g. when afterHero ends with its own footer). */
  hideFooter?: boolean;
  /** Extra controls between logo and share (e.g. section anchors + Begin nu). */
  topNavExtras?: React.ReactNode;
  /** splash = fixed full-viewport shell; scrollable = stack afterHero below hero for normal page scroll. */
  variant?: VideoBackgroundVariant;
}

export function VideoBackground({
  children,
  afterHero,
  hideFooter = false,
  topNavExtras,
  variant = 'splash',
}: VideoBackgroundProps) {
  const isScrollable = variant === 'scrollable';
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const kick = () => {
      el.play().catch(() => {
        /* autoplay blocked until user gesture — poster remains visible */
      });
    };
    kick();
    el.addEventListener('loadeddata', kick);
    return () => el.removeEventListener('loadeddata', kick);
  }, []);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'TheoryCoach',
          text: 'Practice what matters, skip what doesn\'t.',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share canceled');
      }
    }
  };

  return (
    <div
      className={
        isScrollable
          ? 'auth-landing-container auth-landing-container--scrollable'
          : 'auth-landing-container'
      }
    >
      <div
        className={
          topNavExtras ? 'auth-top-nav auth-top-nav--with-extras' : 'auth-top-nav'
        }
      >
        <div className="auth-nav-logo">
          <span className="auth-nav-logo-text">TheoryCoach</span>
        </div>
        {topNavExtras ? <div className="auth-top-nav__extras">{topNavExtras}</div> : null}
        <button
          onClick={handleShare}
          className="auth-share-button"
          aria-label="Share"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
      </div>
      <div className="auth-main-wrapper">
        <div className="auth-video-section">
          <video
            ref={videoRef}
            className="auth-hero-video"
            poster="/images/landing-hero-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden
          >
            <source src="/videos/signin-intro.mp4" type="video/mp4" />
          </video>
          <div className="auth-video-overlay" />
        </div>
        <div className="auth-content-section">
          <div className="auth-content-inner">
            {children}
          </div>
        </div>
      </div>
      {afterHero ?? null}
      {!hideFooter ? (
        <div className="auth-footer">
          <p className="auth-footer-text">
            <a href="/settings/terms" className="auth-footer-link">Voorwaarden</a>
            {' · '}
            <a href="/settings/privacy" className="auth-footer-link">Privacybeleid</a>
            {' · '}
            <a href="/cookies" className="auth-footer-link">Cookies</a>
          </p>
        </div>
      ) : null}
    </div>
  );
}
